<script setup>
// BUILDER-10 #3a/#3c/#3e: the tool form's dedicated "Variables" toolbox. Lists the two token
// kinds as PINK chips (told apart by icon, #3d) that you insert into any field by dragging
// (drop target = every VariableTokenInput) OR by clicking while a field is focused:
//   • Context variables ({{name}}, braces) — system/declared, read-only here.
//   • AI values ({name}, sparkles) — model-filled; declared, described and removed right here.
// AI values are the mode:'ai' rows of the shared form.params array, which we mutate in place
// (same reactive object the Parameters table and the compiler read).
//
// FLOW-13 (SPEC S1, S2, §3.5; PLAN G19): os segredos da conta entram como terceira fileira. A tool
// antiga acha o `{{nome}}` no cofre da conta antes do agente (R2), e o `{{nome}}` se insere como o das
// variáveis. Os chips rosa não ganham irmão: o segredo é o cadeado e o nome, neutros, porque não é
// variável nem valor do agente, e o valor dele nunca chega aqui (só o nome).
// Gate 4 (12a): o segredo era uma pílula neutra, com borda e fundo diferentes do painel. Nunca
// etiqueta com fundo (regra do Murilo): o cadeado e o nome, sem fundo, sem borda, como no cofre.
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { varToken, aiToken, NAME_RE } from '../powerAgents/variableTokens';

const props = defineProps({
  // Context variables: [{ name, kind: 'system' | 'declared' }]
  variables: { type: Array, default: () => [] },
  // The tool's form.params rows. AI values are the mode:'ai' ones. The parent owns the array;
  // we emit add/remove intents (editing a row's description mutates the row object, not the prop).
  params: { type: Array, default: () => [] },
  // Os nomes dos segredos da conta (o cofre), sem valor.
  segredos: { type: Array, default: () => [] },
});
const emit = defineEmits(['addParam', 'removeParam']);

const { t } = useI18n();

const aiValues = computed(() =>
  props.params.filter(p => p.mode === 'ai' && (p.key || '').trim())
);

// ── drag source ──────────────────────────────────────────────────
function makeGhost(text) {
  const el = document.createElement('div');
  el.textContent = text;
  el.style.cssText =
    'position:fixed;top:-100px;left:0;padding:2px 8px;border-radius:6px;' +
    'font:12px monospace;background:#ff13f0;color:#fff;white-space:nowrap;z-index:9999';
  document.body.appendChild(el);
  return el;
}
function onDragStart(e, token) {
  e.dataTransfer.setData('text/plain', token);
  e.dataTransfer.effectAllowed = 'copy';
  const ghost = makeGhost(token);
  e.dataTransfer.setDragImage(ghost, 0, 0);
  setTimeout(() => ghost.remove(), 0);
}

// ── click-insert into the currently focused field ────────────────
// The chip uses @mousedown.prevent so the field keeps focus; dispatching a native 'input'
// event drives the field's Vue @input handler, so the parent's v-model stays in sync.
function insertToken(token) {
  const el = document.activeElement;
  if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  el.value = el.value.slice(0, start) + token + el.value.slice(end);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  const pos = start + token.length;
  el.setSelectionRange(pos, pos);
}

// ── AI value CRUD ────────────────────────────────────────────────
const adding = ref(false);
const draft = reactive({ name: '', description: '' });
const nameValid = computed(() => NAME_RE.test(draft.name));
const nameTaken = computed(() =>
  props.params.some(p => (p.key || '').trim() === draft.name)
);
function startAdd() {
  adding.value = true;
  draft.name = '';
  draft.description = '';
}
function confirmAdd() {
  if (!nameValid.value || nameTaken.value) return;
  emit('addParam', {
    key: draft.name,
    mode: 'ai',
    description: draft.description.trim(),
    example: '',
    value: '',
  });
  adding.value = false;
}
function removeAi(row) {
  emit('removeParam', row);
}
</script>

<template>
  <div
    class="flex flex-col gap-3 p-3 border rounded-lg border-n-brand/30 bg-n-brand/5"
  >
    <div class="flex items-center gap-2">
      <i class="i-lucide-braces text-n-brand" />
      <span
        class="text-xs font-semibold tracking-wide uppercase text-n-slate-11"
      >
        {{ t('POWER_AGENTS.TOOLS.VARS.TITLE') }}
      </span>
    </div>
    <p class="text-xs text-n-slate-10 -mt-1.5">
      {{ t('POWER_AGENTS.TOOLS.VARS.HINT') }}
    </p>

    <!-- Context variables (read-only, insertable) -->
    <div v-if="variables.length" class="flex flex-col gap-1.5">
      <span class="text-xxs font-medium uppercase tracking-wide text-n-slate-9">
        {{ t('POWER_AGENTS.TOOLS.VARS.CONTEXT') }}
      </span>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="v in variables"
          :key="v.name"
          type="button"
          draggable="true"
          class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono rounded-full border cursor-grab active:cursor-grabbing border-n-brand/40 bg-n-brand/10 text-n-brand"
          :title="varToken(v.name)"
          @mousedown.prevent
          @dragstart="onDragStart($event, varToken(v.name))"
          @click="insertToken(varToken(v.name))"
        >
          <i class="i-lucide-braces text-[0.85em]" />
          {{ v.name }}
        </button>
      </div>
    </div>

    <!-- Segredos da conta (FLOW-13): cadeado e nome neutros, inseridos como {{nome}} -->
    <div
      v-if="segredos.length"
      class="flex flex-col gap-1.5"
      data-segredos-da-conta
    >
      <span class="font-medium tracking-wide uppercase text-xxs text-n-slate-9">
        {{ t('POWER_AGENTS.TOOLS.VARS.ACCOUNT_SECRETS') }}
      </span>
      <div class="flex flex-wrap gap-x-4 gap-y-1.5">
        <button
          v-for="nome in segredos"
          :key="nome"
          type="button"
          draggable="true"
          class="inline-flex items-center gap-1.5 p-0 font-mono text-xs bg-transparent border-0 cursor-grab active:cursor-grabbing text-n-slate-12 hover:underline"
          :title="varToken(nome)"
          data-segredo-da-conta
          @mousedown.prevent
          @dragstart="onDragStart($event, varToken(nome))"
          @click="insertToken(varToken(nome))"
        >
          <i class="i-lucide-lock text-n-slate-11" aria-hidden="true" />
          {{ nome }}
        </button>
      </div>
    </div>

    <!-- AI values (create / describe / remove) -->
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center justify-between">
        <span
          class="text-xxs font-medium uppercase tracking-wide text-n-slate-9"
        >
          {{ t('POWER_AGENTS.TOOLS.VARS.AI_VALUES') }}
        </span>
        <button
          v-if="!adding"
          type="button"
          class="text-xs font-medium text-n-brand"
          @click="startAdd"
        >
          {{ t('POWER_AGENTS.TOOLS.VARS.ADD_AI') }}
        </button>
      </div>

      <div v-if="!aiValues.length && !adding" class="text-xs text-n-slate-9">
        {{ t('POWER_AGENTS.TOOLS.VARS.AI_EMPTY') }}
      </div>

      <div
        v-for="row in aiValues"
        :key="row.key"
        class="flex items-center gap-2"
      >
        <button
          type="button"
          draggable="true"
          class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono rounded-full border shrink-0 cursor-grab active:cursor-grabbing border-n-brand/40 bg-n-brand/10 text-n-brand"
          :title="aiToken(row.key)"
          @mousedown.prevent
          @dragstart="onDragStart($event, aiToken(row.key))"
          @click="insertToken(aiToken(row.key))"
        >
          <i class="i-lucide-sparkles text-[0.85em]" />
          {{ row.key }}
        </button>
        <input
          v-model="row.description"
          :placeholder="t('POWER_AGENTS.TOOLS.FORM.PARAM_DESC')"
          class="flex-1 px-2 py-1 text-xs border rounded outline-none border-n-weak bg-n-background"
        />
        <button
          type="button"
          class="px-1 text-n-slate-9 hover:text-n-ruby-10"
          :title="t('POWER_AGENTS.TOOLS.VARS.REMOVE')"
          @click="removeAi(row)"
        >
          <i class="i-lucide-x" />
        </button>
      </div>

      <!-- new AI value mini form -->
      <div
        v-if="adding"
        class="flex flex-col gap-1.5 p-2 border rounded-md border-n-brand/40 bg-n-background"
      >
        <input
          v-model="draft.name"
          :placeholder="t('POWER_AGENTS.TOOLS.FORM.AI_VALUE_NAME')"
          class="px-2 py-1 text-xs font-mono border rounded outline-none border-n-weak bg-n-background"
          @keydown.enter="confirmAdd"
        />
        <input
          v-model="draft.description"
          :placeholder="t('POWER_AGENTS.TOOLS.FORM.AI_VALUE_DESC')"
          class="px-2 py-1 text-xs border rounded outline-none border-n-weak bg-n-background"
          @keydown.enter="confirmAdd"
        />
        <p v-if="draft.name && nameTaken" class="text-xs text-n-ruby-10">
          {{ t('POWER_AGENTS.TOOLS.VARS.NAME_TAKEN') }}
        </p>
        <div class="flex justify-end gap-2">
          <button
            type="button"
            class="text-xs text-n-slate-10"
            @click="adding = false"
          >
            {{ t('POWER_AGENTS.TOOLS.FORM.CANCEL') }}
          </button>
          <button
            type="button"
            class="text-xs font-medium text-n-brand disabled:opacity-40"
            :disabled="!nameValid || nameTaken"
            @click="confirmAdd"
          >
            {{ t('POWER_AGENTS.TOOLS.VARS.ADD') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
